import _ from "lodash";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { AthleteLinks } from "../../../parts/athlete-links";
import Dropdown from "../../../parts/dropdown";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import convertToRiR from "../../../utils/converters/convert-intensity";
import {
  getBestSet,
  getMovementOptions,
  getOutcomeValues,
  getRepMax,
  getRpeTable,
  getValueZone,
  hasRpe,
  shouldHaveE1RM,
} from "../../../utils/exercise-history";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { getUserToRequest } from "../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import NavigationComponent from "./components/NavigationComponent";
import { ReportsBackHandler } from "./components/ReportsBackHandler";

const REP_RANGE = _.range(1, 13);
const RPE_RANGE = _.reverse(_.range(5, 10.5, 0.5));

const RpeTable = ({ locals }) => {
  const { t } = useTranslation("athlete-portal");
  const {
    query,
    activeUser,
    athlete,
    pathname,
    organizationOwner,
    activeUserToRequest,
  } = locals;
  const userToMeasure = getUserToRequest(activeUser, organizationOwner);
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  // All the exercises from the api
  const exercisesData = locals.exerciseHistory
    ? locals.exerciseHistory.exercises
    : [];

  //if the user's preference is rir or rpe
  const intensityMeasure =
    _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";

  //the user's preference is rir?
  const isRiR = intensityMeasure === "RiR";

  const athleteInfo = locals.athlete ? locals.athlete : locals.activeUser;

  //options to the select
  const movementOptions = getMovementOptions(athleteInfo.exerciseLibrary);

  //start date
  const [startDateToShow, setStartDateToShow] = useState(
    query.from
      ? moment(query.from)
      : moment().subtract(6, "months").format("MMM DD, YYYY")
  );

  //end state
  const [endDateToShow, setEndDateToShow] = useState(
    query.to ? moment(query.to) : moment()
  );

  //control the date range tab state (1 month, 3 months... lifetime...)
  const [tabState, setTabState] = useState(
    query.activeRange ? query.activeRange : "six-months-tab"
  );

  const initMovement = {
    label: "Squat",
    primary: "squat",
    secondary: "",
    type: "primary",
    value: "squat",
  };
  //selected option in the select
  const [currentMovement, setCurrentMovement] = useState(
    query.primary
      ? {
          label: query.label,
          primary: query.primary,
          secondary: query.secondary,
          type: query.type,
          value: query.value,
        }
      : initMovement
  );

  //filter movements with load and rpes for the select
  const movementSelect = [];
  movementOptions.forEach((element) => {
    const hasRepsLoad = shouldHaveE1RM(element);
    const hasRepsLoadRpe = hasRepsLoad && hasRpe(element);
    if (hasRepsLoadRpe) {
      movementSelect.push(element);
    }
  });

  //get the sets of the exercises
  const exercisesSets = exercisesData.flatMap((exercise) => {
    return exercise.sets.map((set) => {
      const { load, e1rm, reps, volume, rpe } = getOutcomeValues(set);

      return {
        ...set,
        load,
        e1rm,
        reps,
        volume,
        rpe,
        utcDate: exercise.utcDate,
        repMax: getRepMax(exercise, set),
      };
    });
  });

  //get the best set
  const bestSet = exercisesData.length > 0 ? getBestSet(exercisesSets) : {};

  //unit format
  const unitFormat = query.display
    ? query.display === "metric"
      ? "kg"
      : query.display === "repMax"
        ? "%"
        : "lb"
    : undefined;
  //unit objetct
  const unit = {
    units: query.display
      ? query.display === "metric" || query.display === "repMax"
        ? "metric"
        : "imperial"
      : locals.activeUser.units,
    label: unitFormat
      ? unitFormat
      : locals.activeUser.units === "metric"
        ? "kg"
        : "lb",
    round: userToMeasure.round,
    repMaximums: athleteInfo.repMaximums,
    coachRepMaximums: locals.activeUser.repMaximums,
    intensityMeasure:
      locals.activeUserToRequest.intensityMeasure === "rpe" ? "RPE" : "RiR",
    is1RMPpercentage: query.display === "repMax",
  };

  //rpe table
  const rpeTable = getRpeTable(
    exercisesSets,
    true,
    currentMovement.type,
    bestSet
  );

  // Value of active rep max
  const activeRepMax = query.type
    ? query.type === "primary"
      ? _.get(athlete || activeUser, `repMaximums.${query.primary}`, 0)
      : currentMovement.repMaximum
    : _.get(athlete || activeUser, `repMaximums.${initMovement.primary}`, 0);

  /**
   * Change the selected exercise
   * @param {*} movement
   */
  const handleMovementSelect = (movement) => {
    if (movement) {
      const { secondary, type, primary, label, value } = movement;
      setCurrentMovement(movement);
      if (movement.type === "assistant" || movement.type === "secondary") {
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            label,
            primary,
            type,
            secondary,
            value,
            display: locals.activeUser.units,
          },
        });
      } else {
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            label,
            primary,
            type,
            secondary: "",
            value,
            display: locals.activeUser.units,
          },
        });
      }
    }
  };

  /**
   * Change the date range
   * @param {*} tab
   */
  const handleActiveTab = (tab) => {
    setTabState(tab);
    setDateRange(tab);
  };

  /**
   * Request the exercises with the selected date range
   * @param {*} tab
   */
  const setDateRange = (tab) => {
    const today = moment();
    const lifetimeDate = "2015-12-31T07:57:45.121Z"; //date when MSB starts
    const periodDate =
      tab === "two-weeks-tab"
        ? moment().subtract(14, "days")
        : tab === "one-months-tab"
          ? moment().subtract(1, "months")
          : tab === "three-months-tab"
            ? moment().subtract(3, "months")
            : tab === "six-months-tab"
              ? moment().subtract(6, "months")
              : moment(lifetimeDate);
    const startFormat = periodDate.format("YYYY-MM-DD");
    const endFormat = today.format("YYYY-MM-DD");
    const startDateSend = `${startFormat}T00:00:00Z`;
    const endDateSend = `${endFormat}T00:00:00Z`;
    setStartDateToShow(periodDate.format("MMM DD, YYYY"));
    setEndDateToShow(today.format("MMM DD, YYYY"));

    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        from: startDateSend,
        to: endDateSend,
        activeRange: tab,
      },
    });
  };

  /**
   * Change start date
   * @param {*} e
   */
  const handleStartDateInput = (e) => {
    if (startDateToShow !== moment(e).format("MMM DD, YYYY")) {
      const format = moment(e).format("YYYY-MM-DD");
      const dateSend = `${format}T00:00:00Z`;
      setStartDateToShow(moment(e).format("MMM DD, YYYY"));
      setTabState("");

      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          from: dateSend,
          activeRange: "",
        },
      });
    }
  };
  /**
   * Change end date
   * @param {*} e
   */
  const handleEndDateInput = (e) => {
    if (endDateToShow !== moment(e).format("MMM DD, YYYY")) {
      const format = moment(e).format("YYYY-MM-DD");
      const dateSend = `${format}T00:00:00Z`;
      setEndDateToShow(moment(e).format("MMM DD, YYYY"));
      setTabState("");

      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          to: dateSend,
          activeRange: "",
        },
      });
    }
  };

  return (
    <main role="reports-wrapper">
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <NavigationComponent
        query={query}
        textKey="nav.calendarDayView"
        pathName={locals.pathname}
        classNames="mobile-hidden"
      />
      <article id="rpe-table" role="report-content">
        <header>
          <hgroup>
            <h2 className="mobile-hidden">{t("rpeTable.title")}</h2>
            <ReportsBackHandler title={t("rpeTable.title")} locals={locals} />
            {currentMovement.type !== "assistant" && (
              <p className="s1">
                {query.primary ? _.capitalize(query.primary) : "Squat"} 1RM{" "}
                <span className="clipped">
                  {locals.withWeight(locals.fromMetric(activeRepMax))}
                </span>
              </p>
            )}
          </hgroup>
          <hgroup>
            <div role="calendar-date-selectors" className="mobile-hidden">
              <DateInput
                name="start"
                placeholder={t("reports.startDate")}
                value={startDateToShow}
                onChange={(e) => handleStartDateInput(e)}
              />
              <span>{t("reports.to")}</span>
              <DateInput
                name="end"
                placeholder={t("reports.endDate")}
                value={endDateToShow}
                onChange={(e) => handleEndDateInput(e)}
              />
            </div>
            <div className="selector-container" id="tabsSection">
              <button
                data-button-type={
                  tabState === "two-weeks-tab" ? "primary" : "secondary"
                }
                onClick={() => handleActiveTab("two-weeks-tab")}
                type="button"
              >
                {t("reports.rangeSelector.twoWeeks")}
              </button>
              <button
                data-button-type={
                  tabState === "one-months-tab" ? "primary" : "secondary"
                }
                onClick={() => handleActiveTab("one-months-tab")}
                type="button"
              >
                {t("reports.rangeSelector.oneMonth")}
              </button>
              <button
                data-button-type={
                  tabState === "three-months-tab" ? "primary" : "secondary"
                }
                onClick={() => handleActiveTab("three-months-tab")}
                type="button"
              >
                {t("reports.rangeSelector.threeMonths")}
              </button>
              <button
                data-button-type={
                  tabState === "six-months-tab" ? "primary" : "secondary"
                }
                onClick={() => handleActiveTab("six-months-tab")}
                type="button"
              >
                {t("reports.rangeSelector.sixMonths")}
              </button>
              <button
                data-button-type={tabState === "" ? "primary" : "secondary"}
                onClick={() => handleActiveTab("lifetime-tab")}
                type="button"
              >
                {t("reports.rangeSelector.lifetime")}
              </button>
            </div>
          </hgroup>
        </header>
        <section className="rpe-table-data">
          <Dropdown
            summaryText={currentMovement.label}
            options={movementSelect}
            onSelectFn={(value) => {
              handleMovementSelect(
                movementSelect.find((op) => op.value === value)
              );
            }}
          />
          <div className="flex gap-8 items-center mobile-hidden">
            <div className="max">{t("rpeTable.max")}</div>
            <div className="heavy">{t("rpeTable.heavy")}</div>
            <div className="medium">{t("rpeTable.medium")}</div>
            <div className="light">{t("rpeTable.light")}</div>
            <div className="deload">{t("rpeTable.deload")}</div>
          </div>
          {currentMovement.type === "assistant" ? (
            <ToggleLinksInput
              name="display"
              options={[
                { text: t("rpeTable.kg"), value: "metric" },
                { text: t("rpeTable.lbs"), value: "imperial" },
              ]}
              className={"unit-container"}
            />
          ) : (
            <ToggleLinksInput
              name="display"
              options={[
                { text: t("rpeTable.oneRmPercent"), value: "repMax" },
                { text: t("rpeTable.kg"), value: "metric" },
                { text: t("rpeTable.lbs"), value: "imperial" },
              ]}
              className={"unit-container"}
            />
          )}
        </section>
        <div role="rpe-table-legends" className="only-show-mobile">
          <div className="max">{t("rpeTable.max")}</div>
          <div className="heavy">{t("rpeTable.heavy")}</div>
          <div className="medium">{t("rpeTable.medium")}</div>
          <div className="light">{t("rpeTable.light")}</div>
          <div className="deload">{t("rpeTable.deload")}</div>
        </div>
        <section role="table-wrapper">
          <table role="report-table">
            <thead>
              <tr>
                <th>{intensityMeasure}</th>
                <th>{t("rpeTable.oneRep")}</th>
                <th>2</th>
                <th>3</th>
                <th>4</th>
                <th>5</th>
                <th>6</th>
                <th>7</th>
                <th>8</th>
                <th>9</th>
                <th>10</th>
                <th>11</th>
                <th>12</th>
              </tr>
            </thead>
            <tbody>
              {_.map(RPE_RANGE, (rpe) => {
                if (rpeTable === null) return;
                return (
                  <tr key={rpe}>
                    <td>{convertToRiR(rpe, !isRiR)}</td>
                    {_.map(REP_RANGE, (rep) => {
                      const { value, zone, isActualValue, utcDate } =
                        getValueZone({
                          rpeTable,
                          rep,
                          rpe,
                          unit,
                          bestSet,
                          type: currentMovement.type,
                          primary: currentMovement.primary,
                          activeRepMax,
                        });
                      return (
                        <td key={rep} className={zone}>
                          {isActualValue ? (
                            <a
                              style={{
                                fontWeight: "bold",
                                textDecoration: "none",
                              }}
                              href={
                                `/${
                                  query.athlete
                                    ? `athlete-portal/calendar/?athlete=${query.athlete}&`
                                    : "dashboard/calendar/?"
                                }view=day&date=${utcDate}` +
                                organizationCoachParam
                              }
                            >
                              {value} {""}
                              {unit.label}
                            </a>
                          ) : (
                            `${value}
                            ${unit.label}`
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </article>
    </main>
  );
};

export default RpeTable;
